import { useCallback, useEffect, useMemo, useState } from 'react';
import { bankingAPI, reconciliationAPI } from '../../api';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FormLabel } from '../ui/FormLabel';
import { Card, CardContent } from '../ui/Card';
import {
  CreditCard,
  Landmark,
  AlertTriangle,
  CheckCircle2,
  ArrowRightLeft,
  Clock,
  Search,
} from 'lucide-react';

/**
 * Credit-card settlement modal (plan §6 "Credit cards" / §8 / §9 / §12).
 *
 * Reconciles a credit-card SETTLEMENT -- a payment from a bank account that
 * pays down a credit card balance. The settlement is conceptually one event
 * but ALWAYS shows up as two statement lines:
 *
 *   - bank-side line: outflow on the funding bank account.
 *   - card-side line: payment received on the credit-card account
 *     (reduces card balance).
 *
 * This modal supports two flows from a single source line:
 *
 *   1. Both sides imported -- the user picks a counterpart line on the
 *      opposite-side account (server fetches candidates within ±7 days /
 *      tolerance 0.01). Settlement is created in 'matched' status.
 *
 *   2. Only one side imported -- the user picks the missing side's
 *      ACCOUNT and we park a pending settlement (status pending_card_side
 *      or pending_bank_side) for auto-pair to complete on next import.
 *
 * Anti-double-count (plan §9): the resulting match is match_type=
 * 'credit_card_settlement' with a single allocation target_type=
 * 'credit_card_settlement'. The server posts a withdrawal to bank_transactions
 * on each side at reconcile time -- NO accounting_entries row is created
 * because the original card purchase already booked the expense. This rule
 * is enforced both here (route validation) and at the DB trigger layer.
 */

const formatMoney = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
};

const shortDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '—');

/**
 * Classify an account as 'bank' (asset bank account) or 'card' (liability /
 * credit-card account). Mirrors the server-side classifier so the UI can
 * pre-filter candidate accounts.
 */
function classifyAccountSide(account) {
  if (!account) return null;
  const lc = account.ledger_class;
  const at = account.account_type;
  if (lc === 'asset_bank' || at === 'current_account' || at === 'savings_account') {
    return 'bank';
  }
  if (lc === 'liability' || at === 'credit_card' || at === 'credit_card_account') {
    return 'card';
  }
  return null;
}

export function CreditCardSettlementModal({
  open,
  onOpenChange,
  line,
  bankAccountId,
  onMatched,
}) {
  const lineAmount = Number(line?.amount ?? 0);
  const lineAbs = Math.abs(lineAmount);

  const [sourceAccount, setSourceAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState('');

  const [mode, setMode] = useState('candidate'); // 'candidate' | 'pending'
  const [targetAccountId, setTargetAccountId] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState('');

  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  // Source side (derived from current account ledger class). The opposite
  // side is what counts as a valid counterpart account.
  const sourceSide = useMemo(() => classifyAccountSide(sourceAccount), [sourceAccount]);
  const wantedSide = sourceSide === 'bank' ? 'card' : sourceSide === 'card' ? 'bank' : null;

  const oppositeAccounts = useMemo(
    () =>
      (accounts || []).filter(
        (a) => a.id !== bankAccountId && classifyAccountSide(a) === wantedSide
      ),
    [accounts, bankAccountId, wantedSide]
  );

  const resetFormState = useCallback(() => {
    setMode('candidate');
    setTargetAccountId('');
    setCandidates([]);
    setCandidatesError('');
    setSelectedCandidateId('');
    setReference(line?.reference || '');
    setNotes('');
    setError('');
    setDone(null);
    setBusy(false);
  }, [line?.reference]);

  useEffect(() => {
    if (!open) return;
    resetFormState();
  }, [open, line?.id, resetFormState]);

  // Load tenant accounts so we can show the source side ledger class +
  // populate the opposite-side dropdown for the pending flow.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingAccounts(true);
        setAccountsError('');
        const list = await bankingAPI.getAccounts({ include_inactive: false });
        if (cancelled) return;
        setAccounts(list || []);
        const src = (list || []).find((a) => a.id === bankAccountId) || null;
        setSourceAccount(src);
      } catch (err) {
        if (cancelled) return;
        setAccountsError(
          err?.response?.data?.error || err.message || 'Could not load accounts'
        );
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, bankAccountId]);

  // Auto-load candidate counterpart lines when in candidate mode.
  useEffect(() => {
    if (!open || mode !== 'candidate' || !line?.id) return;
    let cancelled = false;
    (async () => {
      try {
        setCandidatesLoading(true);
        setCandidatesError('');
        const params = { source_statement_line_id: line.id };
        if (targetAccountId) params.target_bank_account_id = targetAccountId;
        const r = await reconciliationAPI.findCreditCardSettlementCounterparts(params);
        if (cancelled) return;
        setCandidates(r?.candidates || []);
        // Auto-select the closest candidate to be helpful but the user
        // can override. Don't auto-pick if there's exactly one and the
        // amount/date diff is suspicious -- keep the user in the loop.
        if ((r?.candidates || []).length === 1) {
          setSelectedCandidateId(r.candidates[0].id);
        } else {
          setSelectedCandidateId('');
        }
      } catch (err) {
        if (cancelled) return;
        setCandidatesError(
          err?.response?.data?.error || err.message || 'Could not load candidates'
        );
        setCandidates([]);
      } finally {
        if (!cancelled) setCandidatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, line?.id, targetAccountId]);

  const sideLabel = sourceSide === 'card' ? 'Credit card' : 'Bank account';
  const wantedSideLabel = wantedSide === 'card' ? 'credit-card' : 'bank';

  const sourceSideValid = sourceSide === 'bank' || sourceSide === 'card';

  const canSubmit = useMemo(() => {
    if (!line || !bankAccountId || !sourceSideValid || lineAbs === 0 || busy) return false;
    if (mode === 'candidate') return !!selectedCandidateId;
    return !!targetAccountId;
  }, [line, bankAccountId, sourceSideValid, lineAbs, busy, mode, selectedCandidateId, targetAccountId]);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        source_statement_line_id: line.id,
        reference: reference || null,
        notes: notes || null,
      };
      if (mode === 'candidate') {
        payload.counterpart_statement_line_id = selectedCandidateId;
      } else {
        payload.target_bank_account_id = targetAccountId;
      }
      const result = await reconciliationAPI.createCreditCardSettlement(payload);
      setDone(result);
      if (onMatched) onMatched(result);
    } catch (err) {
      setError(
        err?.response?.data?.error || err.message || 'Could not create settlement'
      );
    } finally {
      setBusy(false);
    }
  };

  const sourceIcon = sourceSide === 'card' ? CreditCard : Landmark;
  const SourceIcon = sourceIcon;

  return (
    <SlideOver
      open={open}
      onOpenChange={onOpenChange}
      title="Match credit card settlement"
    >
      {!line ? (
        <div className="text-xs text-gray-500">No statement line selected.</div>
      ) : (
        <div className="space-y-4">
          {/* Source line summary */}
          <Card>
            <CardContent className="p-3 text-xs">
              <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <SourceIcon className="h-3.5 w-3.5" />
                {sideLabel} (this side)
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Date</div>
                  <div>{shortDate(line.transaction_date)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Description</div>
                  <div className="truncate">{line.description || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Amount</div>
                  <div
                    className={
                      lineAmount < 0
                        ? 'font-semibold text-red-700 dark:text-red-400'
                        : 'font-semibold text-green-700 dark:text-green-400'
                    }
                  >
                    {formatMoney(lineAbs)}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Reference</div>
                  <div className="truncate">{line.reference || '—'}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Source-side validity check */}
          {!loadingAccounts && !sourceSideValid && (
            <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
              <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  This account is not a bank or credit-card account. Credit card
                  settlements move money between an asset bank account and a
                  liability credit-card account.
                </span>
              </CardContent>
            </Card>
          )}

          {/* Anti-double-count notice */}
          {sourceSideValid && !done && (
            <Card className="border-blue-300 bg-blue-50 dark:bg-blue-950/30">
              <CardContent className="p-3 text-[11px] text-blue-800 dark:text-blue-200">
                <div className="flex gap-2">
                  <ArrowRightLeft className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    Settlements are balance-sheet movements only — the original
                    card purchase already booked the expense, so this match
                    will <strong>not</strong> create an extra P&amp;L entry.
                    Reconciling each side posts a matching <em>bank
                    transaction</em> on its account.
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {accountsError && (
            <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
              <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{accountsError}</span>
              </CardContent>
            </Card>
          )}

          {done ? (
            <Card className="border-green-500 bg-green-50 dark:bg-green-950/30">
              <CardContent className="p-4 text-xs">
                <div className="flex items-center gap-2 font-semibold text-green-800 dark:text-green-200">
                  <CheckCircle2 className="h-4 w-4" />
                  {done?.settlement?.status === 'matched'
                    ? 'Settlement matched (draft).'
                    : `Settlement parked as ${done?.settlement?.status?.replace('_', ' ')}.`}
                </div>
                <div className="mt-2 text-[11px] text-green-800 dark:text-green-200">
                  {done?.settlement?.status === 'matched'
                    ? 'Reconcile both side matches to post the bank transactions on each account.'
                    : `Auto-pair will complete this settlement on the next import that includes the missing ${wantedSideLabel}-side line.`}
                </div>
                <div className="mt-3">
                  <Button size="sm" onClick={() => onOpenChange(false)}>
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : sourceSideValid ? (
            <>
              {error && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </CardContent>
                </Card>
              )}

              {/* Mode toggle */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={mode === 'candidate' ? 'default' : 'outline'}
                  onClick={() => setMode('candidate')}
                >
                  <Search className="mr-1 h-3.5 w-3.5" />
                  Pick counterpart line
                </Button>
                <Button
                  size="sm"
                  variant={mode === 'pending' ? 'default' : 'outline'}
                  onClick={() => setMode('pending')}
                >
                  <Clock className="mr-1 h-3.5 w-3.5" />
                  Park pending ({wantedSideLabel} side)
                </Button>
              </div>

              {/* Candidate flow */}
              {mode === 'candidate' && (
                <div className="space-y-3">
                  <div>
                    <FormLabel>
                      <span className="inline-flex items-center gap-1">
                        Restrict to {wantedSideLabel} account (optional)
                      </span>
                    </FormLabel>
                    <Select
                      value={targetAccountId}
                      onChange={(e) => setTargetAccountId(e.target.value)}
                      disabled={loadingAccounts}
                    >
                      <option value="">All {wantedSideLabel}-side accounts</option>
                      {oppositeAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.account_name}
                        </option>
                      ))}
                    </Select>
                  </div>

                  {candidatesLoading && (
                    <div className="text-xs text-gray-500">Searching counterpart lines…</div>
                  )}
                  {candidatesError && (
                    <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                      <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        <span>{candidatesError}</span>
                      </CardContent>
                    </Card>
                  )}
                  {!candidatesLoading && !candidatesError && candidates.length === 0 && (
                    <div className="rounded border border-dashed border-gray-300 p-3 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-400">
                      No matching counterpart lines found within ±7 days of this
                      line on a {wantedSideLabel}-side account. Switch to
                      <em> Park pending</em> if the other side hasn&apos;t been
                      imported yet.
                    </div>
                  )}
                  {!candidatesLoading && candidates.length > 0 && (
                    <div className="max-h-72 space-y-2 overflow-auto rounded border border-gray-200 p-2 dark:border-gray-700">
                      {candidates.map((c) => {
                        const checked = selectedCandidateId === c.id;
                        return (
                          <label
                            key={c.id}
                            className={`flex cursor-pointer items-start gap-2 rounded p-2 text-xs ${
                              checked
                                ? 'bg-blue-50 ring-2 ring-blue-400 dark:bg-blue-950/30'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="cc-counterpart"
                              value={c.id}
                              checked={checked}
                              onChange={() => setSelectedCandidateId(c.id)}
                              className="mt-0.5"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold">{c.account_name}</span>
                                <span
                                  className={
                                    Number(c.amount) < 0
                                      ? 'font-semibold text-red-700 dark:text-red-400'
                                      : 'font-semibold text-green-700 dark:text-green-400'
                                  }
                                >
                                  {formatMoney(Math.abs(Number(c.amount)))}
                                </span>
                              </div>
                              <div className="text-[11px] text-gray-600 dark:text-gray-400">
                                {shortDate(c.transaction_date)} · {c.description || '—'}
                              </div>
                              {c.reference && (
                                <div className="text-[11px] text-gray-500">
                                  Ref: {c.reference}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Pending flow */}
              {mode === 'pending' && (
                <div className="space-y-3">
                  <div>
                    <FormLabel>
                      <span className="inline-flex items-center gap-1">
                        {wantedSide === 'card' ? (
                          <CreditCard className="h-3 w-3" />
                        ) : (
                          <Landmark className="h-3 w-3" />
                        )}
                        Missing {wantedSideLabel}-side account
                      </span>
                    </FormLabel>
                    <Select
                      value={targetAccountId}
                      onChange={(e) => setTargetAccountId(e.target.value)}
                      disabled={loadingAccounts}
                    >
                      <option value="">
                        {loadingAccounts ? 'Loading accounts…' : `Select ${wantedSideLabel}-side account…`}
                      </option>
                      {oppositeAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.account_name}
                        </option>
                      ))}
                    </Select>
                    {!loadingAccounts && oppositeAccounts.length === 0 && (
                      <div className="mt-1 text-[11px] text-gray-500">
                        No {wantedSideLabel}-side accounts found. Create one on
                        the banking page first.
                      </div>
                    )}
                    <div className="mt-2 text-[11px] text-gray-600 dark:text-gray-400">
                      The settlement will park as
                      {' '}
                      <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">
                        {wantedSide === 'card' ? 'pending_card_side' : 'pending_bank_side'}
                      </code>
                      {' '}
                      and auto-pair with the missing {wantedSideLabel}-side
                      line on the next import.
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <FormLabel>Reference</FormLabel>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. Card payment Apr 2026"
                  />
                </div>
                <div>
                  <FormLabel>Notes</FormLabel>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional note"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button onClick={submit} disabled={!canSubmit}>
                  {busy
                    ? 'Saving…'
                    : mode === 'candidate'
                      ? 'Match settlement'
                      : 'Park pending settlement'}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      )}
    </SlideOver>
  );
}

export default CreditCardSettlementModal;
